import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { CheckSquare, Trash2, Rocket, ArrowRightLeft, X, ChevronDown } from 'lucide-react';

/**
 * Floating action bar that appears when 1+ decisions are selected.
 * Handles bulk delete / move-lane / mark-shipped / clear-selection.
 */
const BulkActionBar = ({
  selectedIds,
  onClear,
  onBulkDelete,
  onBulkMove,
  onBulkMarkShipped,
}) => {
  const count = selectedIds?.length || 0;
  if (count === 0) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-xl border bg-card shadow-lg px-4 py-3 flex items-center gap-3"
      data-testid="bulk-action-bar"
    >
      <div className="flex items-center gap-2">
        <CheckSquare className="h-4 w-4 text-primary" />
        <Badge variant="secondary" className="font-medium" data-testid="bulk-selected-count">
          {count} selected
        </Badge>
      </div>

      <div className="h-4 w-px bg-border" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="bulk-move-trigger">
            <ArrowRightLeft className="h-3.5 w-3.5" />
            Move to lane
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuItem onClick={() => onBulkMove('build_next')} data-testid="bulk-move-build">
            🚀 Build Next
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onBulkMove('fix_now')} data-testid="bulk-move-fix">
            ⚠️ Fix Now
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onBulkMove('ignore')} data-testid="bulk-move-ignore">
            ❌ Ignore
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="outline"
        size="sm"
        onClick={onBulkMarkShipped}
        className="gap-1.5"
        data-testid="bulk-mark-shipped"
      >
        <Rocket className="h-3.5 w-3.5" />
        Mark shipped
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={onBulkDelete}
        className="gap-1.5 text-destructive hover:text-destructive"
        data-testid="bulk-delete"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </Button>

      <div className="h-4 w-px bg-border" />

      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        className="h-8 w-8 p-0"
        aria-label="Clear selection"
        data-testid="bulk-clear"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default BulkActionBar;
